import {
  AssignPhoneCallForm,
  AssignPhoneCallSchema,
  CallDto,
} from '@act/shared/data-transfer-objects';
import {
  CardActions,
  CardContent,
  CircularProgress,
  Dialog,
  Divider,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { callsRequests } from 'redux/requests/calls.request';
import { SelectOption } from 'react-components/components/SearchBar';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import {
  DialogContentContainer,
  DialogTitleBarComponent,
} from '../dialog-forms/components/SharedDialogHookComponents';
import { membersRequests } from 'redux/requests/members.request';
import { Button, FormBuilder } from '@act/storybook';
import { useToast } from '../app/ReusableToast';
import useUnsavedChangesDialog from '../dialog-forms/hooks/useUnsavedChangesDialog.hook';
import {
  removeCallFromState,
  unassignCallSuccess,
  updateSubjectIdSuccess,
} from 'redux/slices/callsSlice.slice';
import { getMemberInfo } from 'redux/slices/memberSlice.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { TrackingProvider } from 'react-components/tracking';

export interface AssignPhoneCallUniqueProps {
  call: CallDto;
  currentCallId?: number;
}

export type AssignPhoneCallDialogProps = GenericDialogProps<boolean> &
  AssignPhoneCallUniqueProps;

export type AssignPhoneCallDialogCallbackInput = {};

/** Last 5 hex chars of id (ignoring hyphens) for disambiguation in typeahead lists. */
function userIdShortSuffix(userId: string): string {
  return userId.replace(/-/g, '').slice(-5);
}

function membersToSelectOptions(
  users: { id: string; firstName?: string; lastName?: string }[]
): SelectOption[] {
  const displayNames = users.map(u =>
    `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim().replace(/\s+/g, ' ')
  );
  const normalizedKeys = displayNames.map(n => n.toLowerCase());
  const nameCounts = new Map<string, number>();
  for (const key of normalizedKeys) {
    nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
  }
  return users.map((u, i) => {
    const baseName = displayNames[i];
    const key = normalizedKeys[i];
    const hasDuplicateName = (nameCounts.get(key) ?? 0) > 1;
    return {
      label: hasDuplicateName
        ? `${baseName} · ${userIdShortSuffix(u.id)}`
        : baseName,
      value: u.id,
    };
  });
}

const AssignPhoneCallDialog = ({
  call,
  currentCallId,
  open,
  setOpen,
  callback,
}: AssignPhoneCallDialogProps) => {
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const dispatch = useAppDispatch();

  const [typeaheadUsers, isLoadingTypeahead, getTypeaheadUsers] = useApiCall(
    membersRequests.getPaginatedMembersForTable
  );
  const [updatedCall, isUpdatingCall, updateSubjectId] = useApiCall(
    callsRequests.updateSubjectId
  );
  const [unassignedCall, isUnassigningCall, unassignCall] = useApiCall(
    callsRequests.unassignCall
  );

  const isReassignFlow = currentCallId !== undefined;

  const { control, handleSubmit, formState, setValue, reset } =
    useForm<AssignPhoneCallForm | null>({
      mode: 'onChange',
      resolver: !isReassignFlow ? zodResolver(AssignPhoneCallSchema) : null,
      defaultValues: !isReassignFlow
        ? {
            interactableId: '',
          }
        : {},
    });

  const isLoading = isUpdatingCall;
  const isSubmitting = isLoading || formState.isSubmitting;

  const typeaheadOptions = useMemo(
    () =>
      typeaheadUsers?.users?.length
        ? membersToSelectOptions(typeaheadUsers.users)
        : [],
    [typeaheadUsers]
  );

  useEffect(() => {
    if (open) {
      void setTypeaheadSearchTerm('');
    }
  }, [open]);

  async function setTypeaheadSearchTerm(searchTerm: string) {
    const overrideFilters = {
      searchTerm,
      'page-start': 0,
      'page-size': 10,
      includeCount: true,
      orderBy: null,
      orderDirection: null,
      includeDetails: true,
    };
    const filterConfig = {
      ...overrideFilters,
    };
    void getTypeaheadUsers({ params: filterConfig });
  }

  const formConfig: FormConfig<AssignPhoneCallForm> = [
    {
      type: 'input',
      input: 'searchBar',
      name: 'interactableId',
      label: isReassignFlow
        ? 'Search by member name (optional)'
        : 'Search by member name',
      props: {
        options: typeaheadOptions,
        isLoading: isLoadingTypeahead,
        onTypeaheadSearch: setTypeaheadSearchTerm,
        trackingName: 'member-search-input',
        onSearch: (searchTerm: string | SelectOption | null) => {
          const value =
            typeof searchTerm === 'string'
              ? searchTerm
              : searchTerm?.value || '';
          setValue('interactableId', value, {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      },
    },
  ];

  useEffect(() => {
    if (updatedCall && !isUpdatingCall) {
      toast('Call assigned successfully!', 'success');

      if (currentCallId !== undefined) {
        dispatch(removeCallFromState(currentCallId));
      }

      dispatch(updateSubjectIdSuccess(updatedCall));
      if (updatedCall.subjectId) {
        dispatch(
          getMemberInfo({ params: { memberId: updatedCall.subjectId } })
        );
      }
      reset();
      setOpen(false);
      callback?.(true);
    }
  }, [updatedCall, isUpdatingCall, currentCallId, reset, setOpen]);

  useEffect(() => {
    if (unassignedCall && !isUnassigningCall) {
      dispatch(unassignCallSuccess(unassignedCall));

      toast('Call unassigned successfully', 'success');
      reset();
      setOpen(false);
      callback?.(true);
    }
  }, [unassignedCall, isUnassigningCall, currentCallId]);

  const onSubmit = async (data: AssignPhoneCallForm | null) => {
    if (isReassignFlow && !data.interactableId) {
      try {
        unassignCall({
          params: { id: call?.id },
        });
      } catch (error) {
        toast('An error occurred. Please try again.', 'error');
      }
    } else {
      try {
        updateSubjectId({
          params: { id: call?.id },
          body: { subjectId: data.interactableId },
        });
      } catch (error) {
        toast('An error occurred. Please try again.', 'error');
      }
    }
  };

  const { isDirty } = formState;
  const handleClose = useCallback(
    (_event?: unknown, _reason?: string) => {
      if (isSubmitting) {
        return;
      }
      if (isDirty) {
        void handleUnsavedChanges(() => {
          reset();
          setOpen(false);
        });
      } else {
        reset();
        setOpen(false);
      }
    },
    [handleUnsavedChanges, isDirty, reset, setOpen, isSubmitting]
  );

  return (
    <TrackingProvider contextName="assign-phone-call-dialog">
      <div style={{ width: '600px' }}>
        <Dialog open={open} onClose={handleClose} fullWidth>
          <DialogContentContainer>
            <DialogTitleBarComponent
              title={
                isReassignFlow ? 'Reassign or unassign call' : 'Assign call'
              }
              handleClose={handleClose}
            />
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardContent sx={{ padding: '12px 24px 0' }}>
                {isReassignFlow ? (
                  <Typography
                    variant="titleMedium"
                    sx={{ display: 'block', marginBottom: '16px' }}
                  >
                    Reassign this call to another member, or just unassign it
                  </Typography>
                ) : (
                  <Typography
                    variant="titleMedium"
                    sx={{ marginBottom: '16px' }}
                  >
                    Select a member for this call
                  </Typography>
                )}
                <FormBuilder
                  config={formConfig}
                  control={control}
                  formState={formState}
                  disabled={isSubmitting}
                />
              </CardContent>
              <Divider sx={{ marginTop: '48px' }} />
              <CardActions
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: isReassignFlow ? 'space-between' : 'right',
                  padding: '8px 24px',
                }}
              >
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    trackingName="cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={!formState.isValid || isSubmitting}
                    debounced
                    onClick={handleSubmit(onSubmit)}
                    type="submit"
                    variant="contained"
                    startIcon={
                      isSubmitting ? <CircularProgress size={20} /> : null
                    }
                    trackingName="assign-call-button"
                  >
                    {isSubmitting
                      ? 'Assigning...'
                      : isReassignFlow
                        ? 'Reassign call'
                        : 'Assign call'}
                  </Button>
                </div>
              </CardActions>
            </form>
          </DialogContentContainer>
        </Dialog>
      </div>
    </TrackingProvider>
  );
};

export default AssignPhoneCallDialog;
